import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { StepsModule } from 'primeng/steps';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { LayoutService } from './core/services/layout.service';
import { SwitchStepService } from 'src/app/core/services/switch-step.service';
import { SharedModule } from './shared/shared.module';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { AuthGuardMessageService } from './core/guards/auth-guard.message.service';
import { AppConfigService } from './core/app-config/app-config.service';
import { RootReducer, StoreService, InterceptorMessageService, TokenInterceptor } from 'projects/dop-core/src/public-api';
import { StoreModule } from '@ngrx/store';
import { DopAuthService, AuthConfigService } from 'projects/dop-core/src/public-api';
import { UserAuthService } from './core/guards/user-auth.service';
import { environment } from 'src/environments/environment';
import { OrderSetupModule } from './features/order-setup/order-setup.module';
import { DopOrderingModule } from 'projects/dop-ordering/src/public-api';
import { SettingsService } from './shared/data-service/settings.service';
import { DopMenuModule } from 'projects/dop-menu/src/public-api';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ErrorModule } from 'projects/dop-core/src/lib/module/error.module';
import { OrderSummaryService } from './features/order-summary/order-summary.service';
import { NgIdleModule } from '@ng-idle/core';
import { MatomoTracker, NgxMatomoTrackerModule } from '@ngx-matomo/tracker';
// Top nav changes container
// import { DopNavbarComponent } from './shared/components/infinite-scroll/dop-navbar.component';
// end

const rxjsServices = [TokenInterceptor, InterceptorMessageService];
const dopSecurityInitConfig = (dopAuthService: DopAuthService) => {
  let internalFileName = '';
  if (window.location.pathname.includes('/downloadFile')) {
    internalFileName = decodeURIComponent(window.location.href.replace(new RegExp('^(?:.*[&\\?]' +
      encodeURIComponent('internalFileName').replace(/[\.\+\*]/g, '\\$&') + '(?:\\=([^&]*))?)?.*$', 'i'), '$1'));
  }
  return () => {
    return dopAuthService.login(environment.configurationPath, internalFileName);
  };
};

export function initializeApp(appConfigService: AppConfigService, matomoTracker: MatomoTracker): () => Promise<any> {
  return async () => {
      await appConfigService.setConfiguration();
      handleMatomoConfiguration(appConfigService, matomoTracker);
  };
}

function handleMatomoConfiguration(appConfigService: AppConfigService, matomoTracker: MatomoTracker){
  const config = appConfigService.getMatomoConfig();

  if (config?.trackers[0].trackerUrl && config?.trackers[0].siteId && config?.scriptUrl) {
    matomoTracker.setTrackerUrl(config.trackers[0].trackerUrl);
    matomoTracker.setSiteId(config.trackers[0].siteId);

    const scriptElement = document.createElement('script');
    scriptElement.type = 'text/javascript';
    scriptElement.async = true;
    scriptElement.src = config.scriptUrl;
    document.head.appendChild(scriptElement);

    console.log('Matomo script loaded:', config.scriptUrl);
  } else {
    console.warn('Matomo configuration is missing or invalid.');
  }
}

// We need a factory, since localStorage is not available during AOT build time.
export function storageFactory(): OAuthStorage {
  return localStorage;
}

@NgModule({
  declarations: [
    AppComponent,
    // Top nav changes container
    // DopNavbarComponent
    // end
  ],
  imports: [
    HttpClientModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ErrorModule,
    StepsModule,
    BreadcrumbModule,
    StoreModule.forRoot({ state: RootReducer }),
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({ logOnly: environment.production }),
    SharedModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    OAuthModule.forRoot(),
    OrderSetupModule,
    DopOrderingModule,
    DopMenuModule,
    NgIdleModule.forRoot(),
    NgxMatomoTrackerModule.forRoot({
      siteId : 0,
      trackerUrl: '',
      scriptUrl: '',
    })
  ],
  providers: [
    LayoutService,
    SwitchStepService,
    StoreService,
    MessageService,
    AuthGuardMessageService,
    DopAuthService,
    AuthConfigService,
    UserAuthService,
    { provide: OAuthStorage, useFactory: storageFactory },
    { provide: APP_INITIALIZER, useFactory: dopSecurityInitConfig, deps: [DopAuthService], multi: true },
    { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [AppConfigService, MatomoTracker], multi: true },
    ...rxjsServices,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    AppConfigService,
    StoreService,
    SettingsService,
    OrderSummaryService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
